import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Brands", path: "/brands" },
  { label: "About Us", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "navbar-scrolled shadow-sm" : "bg-white"
      }`}
    >
      {/* Top bar */}
      <div className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-9 text-xs">
          <span className="font-mono uppercase tracking-widest text-neutral-400">
            Est. 1993 — Jaipur, Rajasthan
          </span>
          <a
            href="tel:+918824893533"
            className="flex items-center gap-1.5 text-neutral-300 hover:text-white transition-colors"
            data-testid="top-bar-phone"
          >
            <Phone className="w-3 h-3" />
            +91 88248 93533
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_rk-automotive-hub/artifacts/wik9fbko_Final%20logo.PNG"
              alt="R.K. Traders Logo"
              className="h-[72px] md:h-[96px] w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`font-heading text-sm uppercase tracking-wider font-semibold transition-colors pb-1 border-b-2 ${
                  location.pathname === link.path
                    ? "text-black border-[#D32F2F]"
                    : "text-neutral-500 border-transparent hover:text-black hover:border-neutral-300"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-enquire"
              className="bg-[#D32F2F] text-white px-6 py-2.5 font-heading uppercase text-sm font-bold tracking-wide hover:bg-[#B71C1C] transition-colors cta-btn"
            >
              Enquire Now
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden bg-white border-t border-neutral-200"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-6 space-y-4">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`block font-heading text-lg uppercase tracking-wider font-semibold ${
                  location.pathname === link.path ? "text-black" : "text-neutral-500"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-cta"
              className="block bg-[#D32F2F] text-white text-center px-6 py-3 font-heading uppercase text-sm font-bold tracking-wide mt-4"
            >
              Enquire Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
